import { CommerceStatusBadge } from "@/components/commerce/commerce-status-badge";
import { formatTimestamp } from "@/components/commerce/shared";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { CustomerSubscriptionService } from "@/gen/omnios/v1/subscription_connect";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { cn } from "@/lib/utils";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useServerGrid } from "@/lib/use-server-grid";
import { createClient } from "@connectrpc/connect";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Pause, Play, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/subscriptions")({
	component: SubscriptionsPage,
});

const subClient = createClient(CustomerSubscriptionService, transport);

type StatusFilter =
	| "all"
	| "active"
	| "paused"
	| "cancelled"
	| "trialing"
	| "past_due";

type Subscription = any;

function SubscriptionsPage() {
	const { t } = useTranslation("commerce");
	useBreadcrumbs([{ label: t("subscriptions") }]);

	const { onGridReady, onStateChanged } = useGridLayout(
		"commerce-subscriptions",
	);

	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [showCreate, setShowCreate] = useState(false);
	const [cancelTarget, setCancelTarget] = useState<string | null>(null);
	const [cancelReason, setCancelReason] = useState("");
	const [form, setForm] = useState({
		customerPartyId: "",
		billingCycle: "monthly",
		currency: "RUB",
		trialEndDate: "",
	});

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<Subscription>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await subClient.listSubscriptions({
				status: statusFilter === "all" ? "" : statusFilter,
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.subscriptions,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		extraFilter: statusFilter,
		colToField: {
			customerPartyId: "customer_party_id",
			billingCycle: "billing_cycle",
			createdAt: "created_at",
		},
	});

	const createMut = useMutation({
		mutationFn: () =>
			subClient.createSubscription({
				customerPartyId: form.customerPartyId,
				billingCycle: form.billingCycle,
				billingInterval: 1,
				currency: form.currency,
				lines: [],
				paymentMethodConfigId: "",
				trialEnd: form.trialEndDate
					? {
							seconds: BigInt(
								Math.floor(new Date(form.trialEndDate).getTime() / 1000),
							),
							nanos: 0,
						}
					: undefined,
			}),
		onSuccess: () => {
			refreshGrid();
			setShowCreate(false);
			setForm({
				customerPartyId: "",
				billingCycle: "monthly",
				currency: "RUB",
				trialEndDate: "",
			});
		},
	});

	const pauseMut = useMutation({
		mutationFn: (id: string) => subClient.pauseSubscription({ id }),
		onSuccess: () => refreshGrid(),
	});

	const resumeMut = useMutation({
		mutationFn: (id: string) => subClient.resumeSubscription({ id }),
		onSuccess: () => refreshGrid(),
	});

	const cancelMut = useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) =>
			subClient.cancelSubscription({ id, reason }),
		onSuccess: () => {
			refreshGrid();
			setCancelTarget(null);
			setCancelReason("");
		},
	});

	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<Subscription>({
			entityLabel: t("subscriptions"),
		});

	const STATUS_TABS: { value: StatusFilter; labelKey: string }[] = [
		{ value: "all", labelKey: "sub_filter_all" },
		{ value: "active", labelKey: "sub_active" },
		{ value: "paused", labelKey: "sub_paused" },
		{ value: "cancelled", labelKey: "sub_cancelled" },
		{ value: "trialing", labelKey: "sub_trialing" },
		{ value: "past_due", labelKey: "sub_past_due" },
	];

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<Subscription>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "customerPartyId",
				headerName: t("sub_col_customer"),
				width: 120,
				cellClass: "font-mono",
				valueFormatter: (p) =>
					p.data?.customerPartyId
						? `${p.data.customerPartyId.slice(0, 8)}...`
						: "—",
			},
			{
				field: "status",
				headerName: t("sub_col_status"),
				width: 130,
				filter: false,
				cellRenderer: (p: { data: Subscription | undefined }) => {
					if (!p.data) return null;
					return (
						<CommerceStatusBadge entity="subscription" status={p.data.status} />
					);
				},
			},
			{
				field: "billingCycle",
				headerName: t("billing_cycle"),
				width: 100,
				valueFormatter: (p) =>
					p.data?.billingCycle ? t(`cycle_${p.data.billingCycle}`) : "—",
			},
			{
				field: "currency",
				headerName: t("pricelist_currency"),
				width: 70,
			},
			{
				headerName: t("current_period"),
				width: 180,
				filter: false,
				valueGetter: (p) => p.data?.currentPeriodStart,
				valueFormatter: (p) => {
					const start = p.data?.currentPeriodStart;
					const end = p.data?.currentPeriodEnd;
					if (!start && !end) return "—";
					return `${formatTimestamp(start)} — ${formatTimestamp(end)}`;
				},
				cellClass: "tabular-nums",
			},
			{
				field: "createdAt",
				headerName: t("sub_col_created"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p) =>
					p.data?.createdAt ? formatTimestamp(p.data.createdAt) : "—",
			},
			{
				colId: "actions",
				headerName: "",
				width: 100,
				sortable: false,
				filter: false,
				resizable: false,
				suppressHeaderMenuButton: true,
				cellRenderer: (p: { data: Subscription | undefined }) => {
					if (!p.data) return null;
					const s = p.data;
					return (
						<div className="flex items-center gap-1">
							{s.status === "active" && (
								<Button
									variant="ghost"
									size="sm"
									className="h-6 px-1.5 text-xs"
									onClick={() => pauseMut.mutate(s.id)}
									disabled={pauseMut.isPending}
								>
									<Pause className="h-3 w-3" />
								</Button>
							)}
							{s.status === "paused" && (
								<Button
									variant="ghost"
									size="sm"
									className="h-6 px-1.5 text-xs"
									onClick={() => resumeMut.mutate(s.id)}
									disabled={resumeMut.isPending}
								>
									<Play className="h-3 w-3" />
								</Button>
							)}
							{(s.status === "active" ||
								s.status === "paused" ||
								s.status === "trialing") && (
								<Button
									variant="ghost"
									size="sm"
									className="h-6 px-1.5 text-xs text-destructive hover:text-destructive"
									onClick={() => setCancelTarget(s.id)}
								>
									<X className="h-3 w-3" />
								</Button>
							)}
						</div>
					);
				},
			},
		],
		[t, pauseMut, resumeMut],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("common:selected", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("subscriptions")}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("common:refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "subscriptions.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setShowCreate(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("common:create")}
				</Button>
			</div>
			{/* Status filter tabs */}
			<div className="flex gap-1 border-b px-2">
				{STATUS_TABS.map((tab) => (
					<Button
						key={tab.value}
						variant="ghost"
						size="sm"
						onClick={() => setStatusFilter(tab.value)}
						className={cn(
							"px-3 py-1.5 rounded-none border-b-2",
							statusFilter === tab.value
								? "border-primary text-primary"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
					>
						{t(tab.labelKey)}
					</Button>
				))}
			</div>

			<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
				<AgGridReact<Subscription>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data?.id}
				/>
			</div>
			<ActionBar />

			{/* Create dialog */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("sub_create")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("sub_col_customer")}</Label>
							<Input
								value={form.customerPartyId}
								onChange={(e) =>
									setForm({ ...form, customerPartyId: e.target.value })
								}
								placeholder="UUID"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("billing_cycle")}</Label>
							<Select
								value={form.billingCycle}
								onValueChange={(v) => setForm({ ...form, billingCycle: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="monthly">{t("cycle_monthly")}</SelectItem>
									<SelectItem value="quarterly">
										{t("cycle_quarterly")}
									</SelectItem>
									<SelectItem value="annual">{t("cycle_annual")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t("pricelist_currency")}</Label>
							<Select
								value={form.currency}
								onValueChange={(v) => setForm({ ...form, currency: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="RUB">RUB</SelectItem>
									<SelectItem value="USD">USD</SelectItem>
									<SelectItem value="EUR">EUR</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t("trial_end")}</Label>
							<Input
								type="date"
								value={form.trialEndDate}
								onChange={(e) =>
									setForm({ ...form, trialEndDate: e.target.value })
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => createMut.mutate()}
							disabled={!form.customerPartyId || createMut.isPending}
						>
							{createMut.isPending && (
								<Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
							)}
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Cancel dialog */}
			<AlertDialog
				open={!!cancelTarget}
				onOpenChange={(o) => !o && setCancelTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("sub_cancel_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("sub_cancel_hint")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="py-2">
						<Label>{t("sub_cancel_reason")}</Label>
						<Input
							className="mt-1"
							value={cancelReason}
							onChange={(e) => setCancelReason(e.target.value)}
							placeholder={t("sub_cancel_reason_placeholder")}
						/>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								cancelTarget &&
								cancelMut.mutate({ id: cancelTarget, reason: cancelReason })
							}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("sub_cancel_confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
